import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { AutocompleteAddressInput } from '@/components/shared/autocomplete-address-input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { CounterNumberInput } from '@/components/shared/counter-number-input';
import { cn } from '@/lib/utils';
import { editAddressSchema, type EditAddressFormData } from './schema';
import { CLEANING_AREA_SIZES_OPTIONS } from '@/constants/options';
import { usePutUserUpdateAddressMutation } from '@/hooks/mutations/use-put-user-update-address.mutation';
import { notifySuccess, notifyError } from '@/lib/notify';
import {
  parseAddressComponents,
  resolvePlacePostalCode,
  geocodeAddress,
} from '@/utils/geocoding';
import { useEffect } from 'react';
import type { Address } from '@/hooks/queries/use-get-user-detail.query';

interface EditAddressFormProps extends React.ComponentProps<'div'> {
  onSuccess?: (data: EditAddressFormData) => void;
  address: Address;
}

export function EditAddressForm({
  className,
  onSuccess,
  address,
  ...props
}: EditAddressFormProps) {
  const updateAddressMutation = usePutUserUpdateAddressMutation();

  const form = useForm<EditAddressFormData>({
    resolver: zodResolver(editAddressSchema),
    defaultValues: {
      addressName: address.address_name || '',
      address: address.address || '',
      city: address.city || '',
      state: address.state || '',
      zipcode: address.zipcode || '',
      country: address.country || '',
      bedroom: address.bedroom || 1,
      bathroom: address.bathroom || 1,
      people: address.people || 1,
      pet: address.pet || 0,
      cleaningAreaSize: address.cleaning_area || '',
      houseDescription: address.house_desc || '',
      latitude: address.latitude ?? 0,
      longitude: address.longitude ?? 0,
    },
  });

  // Update form values when address prop changes
  useEffect(() => {
    form.reset({
      addressName: address.address_name || '',
      address: address.address || '',
      city: address.city || '',
      state: address.state || '',
      zipcode: address.zipcode || '',
      country: address.country || '',
      bedroom: address.bedroom || 1,
      bathroom: address.bathroom || 1,
      people: address.people || 1,
      pet: address.pet || 0,
      cleaningAreaSize: address.cleaning_area || '',
      houseDescription: address.house_desc || '',
      latitude: address.latitude ?? 0,
      longitude: address.longitude ?? 0,
    });
  }, [address, form]);

  const onSubmit = async (data: EditAddressFormData) => {
    try {
      let latitude = data.latitude;
      let longitude = data.longitude;

      // Fallback: geocode from full address if no coords were captured yet
      if ((latitude === 0 || longitude === 0) && data.address) {
        const fullAddress =
          `${data.address}, ${data.city ?? ''}, ${data.state ?? ''} ${data.zipcode ?? ''}, ${data.country ?? ''}`
            .replace(/\s+,/g, ',')
            .trim();
        const geo = await geocodeAddress(fullAddress);
        if (geo) {
          latitude = geo.lat;
          longitude = geo.lng;
        }
      }

      if (latitude === 0 || longitude === 0) {
        notifyError(
          'Could not determine address location. Please pick an address from the suggestions.'
        );
        return;
      }

      const payload = {
        address: data.address,
        city: data.city || '',
        state: data.state || '',
        zipcode: data.zipcode || '',
        country: data.country || '',
        address_name: data.addressName,
        bedroom: data.bedroom,
        bathroom: data.bathroom,
        people: data.people,
        pet: data.pet,
        cleaning_area: data.cleaningAreaSize,
        house_desc: data.houseDescription || '',
        default_address: address.default_address || false,
        latitude: latitude ?? 0,
        longitude: longitude ?? 0,
      };

      await updateAddressMutation.mutateAsync({
        addressId: address.id,
        payload,
      });

      notifySuccess('Address updated successfully!');
      onSuccess?.(data);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Update address error:', error);
      notifyError('Failed to update address. Please try again.');
    }
  };

  return (
    <div className={cn('flex flex-col gap-6', className)} {...props}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Address Name */}
          <FormField
            control={form.control}
            name="addressName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Address Name</FormLabel>
                <FormControl>
                  <Input placeholder="Type address name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Address */}
          <FormField
            control={form.control}
            name="address"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Address</FormLabel>
                <FormControl>
                  <AutocompleteAddressInput
                    value={field.value}
                    onChange={field.onChange}
                    error={!!form.formState.errors.address}
                    onPlaceSelected={place => {
                      const parsed = parseAddressComponents(
                        place.address_components ?? []
                      );
                      form.setValue('address', place.formatted_address || '');
                      form.setValue('city', parsed.city);
                      form.setValue('state', parsed.state);
                      form.setValue('country', parsed.country);
                      const loc = place.geometry?.location;
                      if (loc) {
                        const lat =
                          typeof loc.lat === 'function' ? loc.lat() : loc.lat;
                        const lng =
                          typeof loc.lng === 'function' ? loc.lng() : loc.lng;
                        form.setValue('latitude', lat as number);
                        form.setValue('longitude', lng as number);
                      }
                      void resolvePlacePostalCode(place, parsed).then(zip => {
                        form.setValue('zipcode', zip);
                      });
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Bedroom & Bathroom */}
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="bedroom"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Bedroom</FormLabel>
                  <FormControl>
                    <CounterNumberInput
                      min={0}
                      value={field.value}
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="bathroom"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Bathroom</FormLabel>
                  <FormControl>
                    <CounterNumberInput
                      min={0}
                      value={field.value}
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* People & Pet */}
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="people"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>People</FormLabel>
                  <FormControl>
                    <CounterNumberInput
                      min={0}
                      value={field.value}
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="pet"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Pet</FormLabel>
                  <FormControl>
                    <CounterNumberInput
                      min={0}
                      value={field.value}
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Cleaning Area Size */}
          <FormField
            control={form.control}
            name="cleaningAreaSize"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Enter size of cleaning area in sq ft</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="0-1,000" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {CLEANING_AREA_SIZES_OPTIONS.map(size => (
                      <SelectItem key={size.value} value={size.value}>
                        {size.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* House Description */}
          <FormField
            control={form.control}
            name="houseDescription"
            render={({ field }) => (
              <FormItem>
                <FormLabel>House description</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Provide us with any additional information that the cleaner should know about your home."
                    className="min-h-32"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Submit Button */}
          <div className="flex justify-end">
            <Button
              type="submit"
              size="lg"
              loading={
                form.formState.isSubmitting || updateAddressMutation.isPending
              }
              disabled={
                Object.keys(form.formState.errors).length > 0 ||
                form.formState.isSubmitting ||
                updateAddressMutation.isPending
              }
            >
              {form.formState.isSubmitting || updateAddressMutation.isPending
                ? 'Updating...'
                : 'Update'}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
